import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { PublishingPolicies } from "../components/PublishingPolicies";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useBrand, BrandLogo, brandFooterText } from "../context/BrandContext";
import { FileText, CheckCircle2, XCircle, Clock, LogOut, PenLine, Gauge, Trash2 } from "lucide-react";

function scoreColorClass(score) {
  if (score >= 80) return "text-green-600";
  if (score >= 50) return "text-amber-600";
  return "text-red-600";
}

function daysWaiting(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

function waitTier(days) {
  if (days >= 30) return { badge: "bg-red-50 text-red-700 border-red-200 hover:bg-red-50", color: "#DC2626" };
  if (days >= 14) return { badge: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50", color: "#D97706" };
  return { badge: "bg-green-50 text-green-700 border-green-200 hover:bg-green-50", color: "#16A34A" };
}

function StatusBadge({ post }) {
  if (post.approval) {
    if (post.approval.status === "rejected")
      return (
        <Badge className="rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-50 gap-1">
          <XCircle className="h-3 w-3" /> Rejected
        </Badge>
      );
    return (
      <Badge className="rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-50 gap-1">
        <CheckCircle2 className="h-3 w-3" /> Approved
      </Badge>
    );
  }
  const days = daysWaiting(post.date || post.modified);
  const tier = waitTier(days);
  return (
    <Badge data-testid={`waiting-badge-${post.id}`} className={`rounded-full border gap-1 ${tier.badge}`}>
      <Clock className="h-3 w-3" /> {days === 0 ? "New today" : `Waiting ${days}d`}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const brand = useBrand();
  const off = (m) => (brand.disabled_modules || []).includes(m);
  const [posts, setPosts] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [error, setError] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alsoWp, setAlsoWp] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const startDelete = (a) => {
    setDeleteTarget(a);
    setAlsoWp(true);
    setPin("");
    setPinError("");
    setPinOpen(true);
  };

  const verifyPin = async () => {
    if (pin.length !== 6) return;
    try {
      await api.post("/staff/verify-pin", { pin });
      setPinOpen(false);
      setConfirmOpen(true);
    } catch (err) {
      setPinError(apiError(err, "Incorrect PIN"));
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.post(`/posts/${deleteTarget.post_id}/delete`, { delete_from_wp: alsoWp, pin });
      setApprovals((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setPosts((prev) => (prev || []).filter((x) => x.id !== deleteTarget.post_id));
      toast.success(alsoWp ? "Article removed from WordPress and portal records deleted" : "Portal records deleted");
      setConfirmOpen(false);
      setDeleteTarget(null);
      setPin("");
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    api
      .get("/posts")
      .then((res) => setPosts(res.data.posts))
      .catch((err) => setError(apiError(err, "Failed to load articles")));
    api.get("/approvals").then((res) => setApprovals(res.data.approvals)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="border-b border-black/[0.08] bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-9 w-auto" textClass="text-2xl" />
            <span className="hidden sm:inline font-mono-accent text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-l border-black/10 pl-3">
              Content Review Portal
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="header-user-name" className="text-sm text-neutral-600 hidden sm:inline">
              {user?.display_name}
            </span>
            <Button
              data-testid="logout-button"
              variant="outline"
              size="sm"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="rounded-full border-black/10 active:scale-95"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
        <div className="mb-10 fade-in">
          <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
            Welcome back
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">
            Articles in review
          </h1>
          <p data-testid="dashboard-site-domain" className="font-mono-accent text-sm text-neutral-600 mt-2">
            {user?.is_demo ? "Demo Mode" : (user?.site_url || "").replace(/^https?:\/\//i, "").replace(/^www\./i, "")}
          </p>
        </div>

        <Tabs defaultValue="queue">
          <TabsList className="rounded-full bg-black/[0.04] p-1 mb-8">
            <TabsTrigger data-testid="tab-review-queue" value="queue" className="rounded-full px-5">
              Review Queue {posts ? `(${posts.length})` : ""}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-approvals-log" value="approvals" className="rounded-full px-5">
              Approvals Log {approvals.length ? `(${approvals.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="queue">
            {error && (
              <p data-testid="posts-error-message" className="text-sm text-red-600 mb-6">
                {error}
              </p>
            )}
            {posts === null && !error && (
              <div className="flex justify-center py-24">
                <div className="im-spinner" />
              </div>
            )}
            {posts && posts.length === 0 && (
              <div className="text-center py-24 text-neutral-500" data-testid="empty-queue-message">
                <FileText className="h-10 w-10 mx-auto mb-4 text-neutral-300" />
                No articles are currently in your review queue.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(posts || []).map((post, i) => (
                <motion.button
                  key={post.id}
                  data-testid={`post-card-${post.id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => navigate(`/article/${post.id}`)}
                  style={{ borderLeft: `4px solid ${post.approval ? (post.approval.status === "rejected" ? "#DC2626" : "#16A34A") : waitTier(daysWaiting(post.date || post.modified)).color}` }}
                  className="text-left bg-white border border-black/[0.08] rounded-lg p-6 flex flex-col h-full hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200 group"
                >
                  {post.thumbnail && (
                    <img
                      src={post.thumbnail}
                      alt=""
                      className="w-full h-36 object-cover rounded-md mb-4"
                      data-testid={`post-thumbnail-${post.id}`}
                    />
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <StatusBadge post={post} />
                    <div className="flex items-center gap-3">
                      {typeof post.seo_score === "number" && (
                        <span
                          title="SEO score"
                          data-testid={`post-score-${post.id}`}
                          className={`inline-flex items-center gap-1 text-xs font-semibold ${scoreColorClass(post.seo_score)}`}
                        >
                          <Gauge className="h-3.5 w-3.5" /> {Math.round(post.seo_score)}
                        </span>
                      )}
                      <span className="font-mono-accent text-[10px] text-neutral-400">
                        {post.word_count} words
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight mb-2 group-hover:text-[#0C4A8F] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-neutral-500 leading-relaxed line-clamp-3 flex-1">
                    {post.excerpt}...
                  </p>
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-black/[0.06]">
                    <span className="text-xs text-neutral-400">Updated {fmtDate(post.modified)}</span>
                    <span className="text-xs font-semibold text-[var(--brand-primary)] inline-flex items-center gap-1">
                      <PenLine className="h-3 w-3" /> {post.approval ? "View" : "Review & Edit"}
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
          {!off("publishing-policies") && (
            <PublishingPolicies />
          )}
          </TabsContent>

          <TabsContent value="approvals">
            {approvals.length === 0 ? (
              <div className="text-center py-24 text-neutral-500" data-testid="empty-approvals-message">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-4 text-neutral-300" />
                No approvals yet. Approved articles will appear here with their signatures.
              </div>
            ) : (
              <div className="bg-white border border-black/[0.08] rounded-xl overflow-hidden">
                {approvals.map((a, i) => (
                  <div
                    key={a.id}
                    data-testid={`approval-row-${a.post_id}`}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-6 py-5 ${
                      i < approvals.length - 1 ? "border-b border-black/[0.06]" : ""
                    }`}
                  >
                    <div>
                      <p className="font-heading font-bold tracking-tight">{a.post_title}</p>
                      <p className="text-sm text-neutral-500 mt-0.5">
                        {a.status === "rejected" ? (
                          <>Rejected by <span className="font-semibold text-neutral-700">{a.signed_by_username}</span></>
                        ) : (
                          <>Signed by <span className="font-semibold text-neutral-700">{a.signature_name}</span> ({a.signed_by_username})</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="text-right">
                        <p className="font-mono-accent text-xs text-neutral-500">
                          {new Date(a.signed_at).toLocaleString()}
                        </p>
                        {a.status === "rejected" ? (
                          <Badge className="rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-50 mt-1">
                            Rejected
                          </Badge>
                        ) : (
                          <Badge className="rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-50 mt-1">
                            Approved
                          </Badge>
                        )}
                      </div>
                      <Button
                        data-testid={`delete-article-button-${a.post_id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => startDelete(a)}
                        title="Delete this article (staff only)"
                        className="text-neutral-300 hover:text-red-600 hover:bg-red-50 rounded-full h-9 w-9 p-0 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        {brandFooterText(brand) && (
          <p className="mt-16 text-center text-xs text-neutral-400" data-testid="brand-footer">
            {brandFooterText(brand)}
          </p>
        )}
      </main>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="sm:max-w-xs rounded-xl" data-testid="staff-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-black tracking-tighter">Staff PIN required</DialogTitle>
            <DialogDescription>
              Deleting articles is limited to {brand.display_name} staff. Enter the 6-digit PIN to continue.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="staff-pin-input"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && verifyPin()}
            placeholder="••••••"
            className="h-11 rounded-lg text-center tracking-[0.5em] font-mono-accent"
          />
          {pinError && (
            <p data-testid="staff-pin-error" className="text-xs text-red-600">{pinError}</p>
          )}
          <Button
            data-testid="staff-pin-submit-button"
            disabled={pin.length !== 6}
            onClick={verifyPin}
            className="w-full h-10 rounded-full bg-[var(--brand-primary)] hover:opacity-90 text-white"
          >
            Unlock
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-black tracking-tighter">
              Do you want to remove this article?
            </DialogTitle>
            <DialogDescription className="pt-1">
              This permanently deletes the portal records for{" "}
              <span className="font-semibold text-neutral-700">"{deleteTarget?.post_title}"</span> — the
              approval, signature and chat history.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="delete-wp-checkbox"
              checked={alsoWp}
              onCheckedChange={(v) => setAlsoWp(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-neutral-600 leading-relaxed">
              Also remove the article from WordPress (the draft is moved to the WordPress Trash)
            </span>
          </label>
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              data-testid="delete-cancel-button"
              onClick={() => setConfirmOpen(false)}
              className="flex-1 rounded-full border-black/10"
            >
              Cancel
            </Button>
            <Button
              data-testid="delete-confirm-button"
              disabled={deleting}
              onClick={doDelete}
              className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
